'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useReactiveRef } from './useReactiveRef';
import { useDispatch, useSelector } from 'react-redux';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logger';
import { getOrCreateCallId } from '@/lib/calls/getOrCreateCallId';
import { NoteItem, setNotes } from '@/store/slices/notesSlice';
import { RootState } from '@/store/store';

/**
 * Notes Real-Time Sync Hook
 *
 * Provides real-time Supabase sync for call notes.
 * - Debounced writes (1s) to prevent rate limiting during typing
 * - Hydrates from Supabase on mount
 * - Last-write-wins conflict resolution with timestamps
 *
 * This replaces the archive-only save pattern for notes.
 */

const SYNC_DEBOUNCE_MS = 1000; // Longer debounce for typing

interface UseNotesSyncOptions {
  /** Call ID (room name) for scoping */
  callId: string;
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
  /** Custom debounce time in ms */
  debounceMs?: number;
}

interface NotesSyncState {
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  error: Error | null;
  dbCallId: string | null;
}

// Database schema for note content
interface DbNoteItem {
  id: string;
  content: string;
  created_at_ms: number;
  updated_at_ms: number;
}

/**
 * Convert Redux NoteItem to DB schema format
 */
function toDbFormat(note: NoteItem): DbNoteItem {
  return {
    id: note.id,
    content: note.content,
    created_at_ms: note.createdAt,
    updated_at_ms: note.updatedAt,
  };
}

/**
 * Convert DB format back to Redux NoteItem
 */
function fromDbFormat(item: DbNoteItem): NoteItem {
  return {
    id: item.id,
    content: item.content,
    createdAt: item.created_at_ms,
    updatedAt: item.updated_at_ms,
  };
}

/**
 * Coerce whatever `notes.content` actually holds into note items.
 *
 * WHY THIS EXISTS: `content` is jsonb, so the column can hold any JSON — and the reload path used
 * to do `notesData.content as DbNoteItem[]` followed by `.map(...)`. A cast is not a check. Any row
 * whose content is a string or a bare object (legacy rows, a dataset loaded from elsewhere, a
 * hand-written row) made `.map` undefined and threw `content.map is not a function`, which the
 * catch below turns into a hard notes-load failure: the officer's notes panel comes up empty with
 * an error and local notes stop syncing. The panel must degrade, not break, because the same schema
 * is meant to run against any environment's data and we do not control what is already in it.
 *
 * The tolerance here is deliberately the SAME as the existing parser in
 * lib/hooks/call-metrics/useCallMetrics.ts (array -> items, single object -> one item, anything
 * else -> ignored). Matching it keeps one shape convention across the app instead of inventing a
 * second. A jsonb string is therefore ignored rather than shown as a note: note bodies render as
 * plain text, so surfacing a stored HTML document as note text would print raw markup at the
 * officer — worse than showing nothing, and a product decision this guard has no business making.
 */
export function coerceDbNotes(raw: unknown, callId: string): DbNoteItem[] {
  const items = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? [raw] : [];

  if (items.length === 0) {
    if (raw !== null && raw !== undefined && !Array.isArray(raw)) {
      logger.warn('useNotesSync', 'Ignoring notes.content of unusable shape', {
        callId,
        shape: typeof raw,
      });
    }
    return [];
  }

  return items
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map((item, index) => ({
      // A missing id would collide in the merge Set below and break React keys. Fall back to a
      // value derived from the position, which is stable for the same stored row.
      id: typeof item.id === 'string' && item.id ? item.id : `${callId}-note-${index}`,
      content: typeof item.content === 'string' ? item.content : '',
      created_at_ms: typeof item.created_at_ms === 'number' ? item.created_at_ms : 0,
      updated_at_ms: typeof item.updated_at_ms === 'number' ? item.updated_at_ms : 0,
    }));
}

/**
 * Hook to sync notes to Supabase in real-time
 */
export function useNotesSync(
  options: UseNotesSyncOptions,
): NotesSyncState & { reloadFromSupabase: () => Promise<void> } {
  const { callId, enabled = true, debounceMs = SYNC_DEBOUNCE_MS } = options;

  const dispatch = useDispatch();
  const { user, isLoading: authLoading } = useAuth();
  const supabase = createClient();

  const notes = useSelector((state: RootState) => state.notes.notes);

  // Use ref to read current notes without creating dependency loops.
  // loadFromSupabase needs current notes for merging, but must NOT depend on
  // `notes` directly or it recreates on every dispatch → infinite fetch loop.
  const notesRef = useRef(notes);
  notesRef.current = notes;

  // Refs for tracking state without triggering re-renders
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncedHashRef = useRef<string | null>(null);
  const dbCallIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const hasLoadedRef = useRef(false);
  const stateRef = useReactiveRef<NotesSyncState>({
    isLoading: true,
    isSyncing: false,
    lastSyncTime: null,
    error: null,
    dbCallId: null,
  });

  /**
   * Get or create the DB call ID (UUID) for this call
   */
  const getOrCreateDbCallId = useCallback(async (): Promise<string | null> => {
    if (dbCallIdRef.current) {
      return dbCallIdRef.current;
    }

    try {
      const dbCallId = await getOrCreateCallId(supabase, callId, {
        userId: user?.id ?? null,
      });
      if (dbCallId) {
        dbCallIdRef.current = dbCallId;
      }
      return dbCallId;
    } catch (err: unknown) {
      logger.error('useNotesSync', 'Failed to get/create call ID', { error: err, callId });
      return null;
    }
  }, [callId, supabase, user?.id]);

  /**
   * Sync notes to Supabase
   */
  const syncToSupabase = useCallback(async (notesArray: NoteItem[]) => {
    if (!enabled || !callId) return;

    // Compute hash to avoid redundant syncs
    const hash = JSON.stringify(notesArray.map(n => ({
      id: n.id,
      c: n.content,
      u: n.updatedAt,
    })));

    if (hash === lastSyncedHashRef.current) {
      logger.debug('useNotesSync', 'Skipping sync - no changes');
      return;
    }

    stateRef.current = { ...stateRef.current, isSyncing: true };

    try {
      const dbCallId = await getOrCreateDbCallId();
      if (!dbCallId) {
        throw new Error('Failed to get/create call ID');
      }

      // Convert to DB format
      const dbContent = notesArray.map(toDbFormat);

      // Upsert notes
      const { error } = await supabase
        .from('notes')
        .upsert({
          call_id: dbCallId,
          content: dbContent,
        }, {
          onConflict: 'call_id',
        });

      if (error) {
        throw error;
      }

      lastSyncedHashRef.current = hash;
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        lastSyncTime: Date.now(),
        error: null,
        dbCallId,
      };

      logger.debug('useNotesSync', 'Synced notes to Supabase', {
        callId,
        count: notesArray.length,
      });
    } catch (err: unknown) {
      logger.error('useNotesSync', 'Failed to sync to Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, getOrCreateDbCallId, supabase]);

  /**
   * Load notes from Supabase (on mount)
   */
  const loadFromSupabase = useCallback(async (opts?: { force?: boolean }) => {
    if (!enabled || !callId || (!opts?.force && hasLoadedRef.current)) {
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }

    hasLoadedRef.current = true;

    try {
      const { data: callData } = await supabase
        .from('calls')
        .select('id')
        .eq('call_id', callId)
        .maybeSingle();

      if (!callData?.id) {
        stateRef.current = { ...stateRef.current, isLoading: false };
        return;
      }

      dbCallIdRef.current = callData.id;

      const { data: notesData, error } = await supabase
        .from('notes')
        .select('content')
        .eq('call_id', callData.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (notesData?.content && mountedRef.current) {
        const noteItems = coerceDbNotes(notesData.content, callData.id).map(fromDbFormat);

        // Merge strategy: use Supabase as source of truth but keep any
        // locally-added notes that aren't in DB yet (by ID)
        const dbNoteIds = new Set(noteItems.map(n => n.id));
        const currentNotes = notesRef.current;
        const localOnlyNotes = currentNotes.filter(n => !dbNoteIds.has(n.id));

        // Merge: DB notes + local-only notes, sorted by createdAt
        const merged = [...noteItems, ...localOnlyNotes].sort(
          (a, b) => a.createdAt - b.createdAt
        );

        dispatch(setNotes(merged));
        lastSyncedHashRef.current = JSON.stringify(merged.map(n => ({
          id: n.id,
          c: n.content,
          u: n.updatedAt,
        })));

        logger.info('useNotesSync', 'Loaded from Supabase', {
          count: merged.length,
          fromDb: noteItems.length,
          localOnly: localOnlyNotes.length,
        });
      }

      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        dbCallId: callData.id,
      };
    } catch (err: unknown) {
      logger.error('useNotesSync', 'Failed to load from Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, supabase, dispatch]);

  // #725: force a re-sync from Supabase on LiveKit reconnect. The merge keeps
  // un-flushed local-only notes (by ID via notesRef) while backfilling anything
  // another officer wrote during the disconnect window. isReloadingRef collapses
  // back-to-back reconnects into a single in-flight fetch.
  const isReloadingRef = useRef(false);
  const reloadFromSupabase = useCallback(async () => {
    if (isReloadingRef.current) return;
    isReloadingRef.current = true;
    try {
      await loadFromSupabase({ force: true });
    } finally {
      isReloadingRef.current = false;
    }
  }, [loadFromSupabase]);

  // Load from Supabase on mount
  useEffect(() => {
    if (!enabled || authLoading) return;

    loadFromSupabase();
  }, [enabled, authLoading, loadFromSupabase]);

  // Debounced sync when notes change
  useEffect(() => {
    if (!enabled || !callId) return;

    // Cancel pending sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Schedule new sync (even for empty array to sync deletions)
    syncTimeoutRef.current = setTimeout(() => {
      syncToSupabase(notes);
    }, debounceMs);
  }, [enabled, callId, notes, debounceMs, syncToSupabase]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return { ...stateRef.current, reloadFromSupabase };
}

/**
 * Hook to hydrate notes from storage
 */
export function useNotesHydration(callId: string): {
  hydrate: () => Promise<void>;
  isHydrated: boolean;
} {
  const dispatch = useDispatch();
  const supabase = createClient();
  const hydratedRef = useRef(false);

  const hydrate = useCallback(async () => {
    if (hydratedRef.current || !callId) return;

    try {
      const { data: callData } = await supabase
        .from('calls')
        .select('id')
        .eq('call_id', callId)
        .maybeSingle();

      if (callData?.id) {
        const { data: notesData } = await supabase
          .from('notes')
          .select('content')
          .eq('call_id', callData.id)
          .maybeSingle();

        if (notesData?.content) {
          const notes = coerceDbNotes(notesData.content, callData.id).map(fromDbFormat);
          dispatch(setNotes(notes));
          logger.info('useNotesHydration', 'Hydrated from Supabase', { count: notes.length });
        }
      }

      hydratedRef.current = true;
    } catch (err: unknown) {
      logger.error('useNotesHydration', 'Hydration failed', { error: err });
    }
  }, [callId, dispatch, supabase]);

  return {
    hydrate,
    isHydrated: hydratedRef.current,
  };
}

export default useNotesSync;
